import { ChangeDetectorRef, Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Apollo } from 'apollo-angular';
import 'brace';
import 'brace/mode/json';
import 'brace/mode/text';
import 'brace/theme/github';
import { Buffer } from 'buffer';
import { TableLazyLoadEvent } from 'primeng/table';
import { NXDataSourcePrime } from '../../services/dataSource/NXDataSourcePrime';
import { EventsDataSource } from './eventsDataSource';
import { MenuItem } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast/toast.service';
import { EventService } from '../../services/event/eventService';

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss']
})
export class EventsComponent implements OnInit, OnDestroy{

  dialogRef: DynamicDialogRef | undefined;

  public menuItems: MenuItem[] = [];

  public sortProperty: string;
  public defaultSortProperty: string = 'eventDatetime';
  public sortDirection: string;
  public defaultSortDirection: string = 'desc';
  public pageNumber = 0;
  public pageSize = 15;
  public sortModel = { label: 'Datum absteigend', fieldName: 'eventDatetime', direction: "desc"};
  public sortOptions = [
    { label: 'coreModuleModule.eventsComponent.filter.dateDesc', fieldName: 'eventDatetime', direction: "desc", key: 'eventDatetimeDesc'},
    { label: 'coreModuleModule.eventsComponent.filter.dateAsc', fieldName: 'eventDatetime', direction: "asc", key: 'eventDatetimeAsc'},
  ];

  public dataSource: EventsDataSource;
  public loading = false;

  // ace config so that it only takes the hight of the content
  editorConfig = {
    maxLines: Infinity,
    highlightActiveLine: false,
    highlightGutterLine: false
  }

  constructor(
    private apollo: Apollo,
    @Inject('env') public environment: any,
    private cdRef: ChangeDetectorRef,
    private confirmDialogService: ConfirmDialogService,
    private translate: TranslateService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private eventService: EventService,
  ) {
    this.sortModel = this.sortOptions[0];
  }

  ngOnInit():void {
    this.dataSource = new EventsDataSource(this.apollo, this.environment, this);
    this.dataSource.loading.subscribe((loading) => {
      this.loading = loading;
    });
    this.dataSource.loadEvents({
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortProperty: this.sortProperty ? this.sortProperty : this.defaultSortProperty,
      sortDirection: this.sortDirection ? this.sortDirection : this.defaultSortDirection,
    });
  }

  refetchEvents(): void {
    this.dataSource.loadEvents({
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortProperty: this.sortProperty ? this.sortProperty : this.defaultSortProperty,
      sortDirection: this.sortDirection ? this.sortDirection : this.defaultSortDirection,
    });
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  loadData(event: TableLazyLoadEvent) {
    this.loading = true;

    // sorting
    if (event?.sortField !== undefined && typeof event?.sortField == 'string') {
      this.sortProperty = event.sortField;
    }
    if (event?.sortOrder !== undefined) {
      this.sortDirection = event.sortOrder === 1 ? 'asc' : 'desc';
    }

     // pagination
     this.pageNumber = event?.first !== undefined ? event.first / event.rows : 0;
     this.pageSize = event?.rows !== undefined ? event.rows : 15;

     this.refetchEvents();
   }

  sort(event: any) {
    if(event?.value?.direction && event?.value?.fieldName){
      this.sortProperty = event.value.fieldName;
      this.sortDirection = event.value.direction;
    } else {
      this.sortProperty = this.defaultSortProperty;
      this.sortDirection = this.defaultSortDirection;
    }
    this.cdRef.detectChanges();
  }

  translateSortDirectionToSortOrder(sortDirection: string): number {
    return NXDataSourcePrime.translateSortDirectionToSortOrder(sortDirection);
  }

  // -> check
  getJSON(base64: string) {
    if (base64 && base64?.length > 0) {
      let decodedData = Buffer.from(base64, 'base64').toString();
      let jsonData = JSON.parse(decodedData);
      return jsonData;
    }
    return '_';
  }

  getString(base64: string) {
    if (base64 && base64?.length > 0) {
      let stringData = Buffer.from(base64, 'base64').toString();
      return stringData;
    }
    return '_';
  }

  isJsonString(str: string): boolean {
    try {
      this.getJSON(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  copyToClipboard(stringToCopy: string): void {
    navigator.clipboard.writeText(stringToCopy);
  }

  copyRequestContent(requestContent: string) {
    let base64 = requestContent;
    if (base64 && base64?.length > 0) {
      let string = Buffer.from(base64, 'base64').toString();
      let toCopy = this.isJsonString(string) ? JSON.stringify(JSON.parse(string)) : string;
      this.copyToClipboard(toCopy);
    }
  }

  getCodeModel(value: string) {
    try {
      if (value && value?.length > 0) {
        const decodedData = Buffer.from(value, 'base64').toString('utf-8');
        const jsonData = JSON.parse(decodedData);
        return JSON.stringify(jsonData, null, 2);
      }
      else {
        return "";
      }
    }
    catch (error) {
      console.error('Error decoding or parsing Base64 JSON:', error);
      return  "";
    }
  }

  calculateSeverity(status: string) {
    // OPEN, IN_DELIVERY, FAILED, CLOSED
    // switch case for OPEN, IN_DELIVERY, FAILED, CLOSED
    switch (status) {
      case 'OPEN':
        return 'info';
      case 'IN_DELIVERY':
        return 'info';
      case 'FAILED':
        return 'danger';
      case 'CLOSED':
        return 'success';
    }
    return 'info';
  }

  setMenuItems(): void {
    this.menuItems = [];
    console.log('setMenuItems', this.dataSource?.selectedEvent);
    this.menuItems.push({
      label: this.translate.instant('coreModuleModule.eventsComponent.details.menu.resent'),
      disabled: !this.dataSource?.selectedEvent?.state || this.dataSource?.selectedEvent?.state !== 'FAILED',
      command: () => {
        this.resendEvent();
      }
    });

    this.menuItems.push({
      label: this.translate.instant('coreModuleModule.eventsComponent.details.menu.close'),
      disabled: !(this.dataSource?.selectedEvent?.state && (this.dataSource?.selectedEvent?.state === 'FAILED' || this.dataSource?.selectedEvent?.state === 'OPEN')),
      command: () => {
        this.closeEvent();
      }
    });
  }

  resendEvent(): void {
    this.dialogRef = this.confirmDialogService.open({
      message: this.translate.instant(
        'coreModuleModule.eventsComponent.details.resendEventDialogMessage',
        { eventId: this.dataSource?.selectedEvent?.id }
      ),
      severity: ConfirmDialogSeverity.WARN,
      header: 'coreModuleModule.eventsComponent.details.resendEventDialogHeader',
    });
    this.dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
      if (result === ConfirmDialogEventType.CONFIRM) {
        this.loadingService.setLoading(true);
        this.eventService.resend(this.dataSource?.selectedEvent?.id, () => {
          this.refetchEvents();
          this.loadingService.setLoading(false);
          this.toastService.addToastWithMessage('coreModuleModule.eventsComponent.details.eventResentToastMessage');
        });
      };
    })
  }

  closeEvent(): void {
    this.dialogRef = this.confirmDialogService.open({
      message: this.translate.instant(
        'coreModuleModule.eventsComponent.details.closeEventDialogMessage',
        { eventId: this.dataSource?.selectedEvent?.id }
      ),
      severity: ConfirmDialogSeverity.WARN,
      header: 'coreModuleModule.eventsComponent.details.closeEventDialogHeader',
    });
    this.dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
      if (result === ConfirmDialogEventType.CONFIRM) {
        this.loadingService.setLoading(true);
        this.eventService.close(this.dataSource?.selectedEvent?.id, () => {
          this.refetchEvents();
          this.loadingService.setLoading(false);
          this.toastService.addToastWithMessage('coreModuleModule.eventsComponent.details.eventClosedToastMessage');
        });
      };
    })
  }

  getCurrentPageReportTemplate(): string {
    return this.translate.instant('coreModuleModule.eventsComponent.table.currentPageReportTemplate', { first: '{first}', last: '{last}', totalRecords: '{totalRecords}' });
  }
}
